import React from "react";

import { SkeletonBox } from "~/components/skeleton/box";
import { StatArea } from "~/components/stat-area";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";

import { LAST_5_MINUTES, type TimeRange } from "../../app/state/view/timeline/TimeRange";
import { isHTTPError } from "../../generic/request";
import { type BatchedPromQLRequest, type RangePromQLResult, queryRangePromQL } from "../../queries/metrics/batch";
import { useQuery } from "../../queries/react-query";

import { CellError } from "./_cell-error";

export interface MetricsChartProps extends BatchedPromQLRequest {
	className?: string;
	width: number;
	height: number;
	query: undefined | string;
	step: undefined | number | string;
	timestamp: undefined | Time;
	timeRange: undefined | TimeRange;
}

export const MetricsChart: React.FC<MetricsChartProps> = ({
	className,
	width,
	height,
	batch,
	query,
	step,
	timestamp,
	timeRange,
}) => {
	const { error, data } = useQuery({
		enabled: Boolean(step) && query != null && timeRange != null,
		refetchOnMount: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		refetchOnTimelineSummaryUpdate: true,

		select: React.useCallback(
			(result: RangePromQLResult) => ({
				result,
				// the interval is directly related to the query result
				interval: (timeRange ?? LAST_5_MINUTES).toInterval(),
			}),
			[timeRange],
		),

		...queryRangePromQL({
			aligned: false,
			batch,
			step: step ?? "",
			query: query ?? "",
			timeRange: timeRange ?? LAST_5_MINUTES,
		}),
	});

	if (error != null) {
		return (
			<div className={cx("flex items-center justify-center", className)} style={{ width, height }}>
				<CellError message={isHTTPError(error) ? error.message : "Unexpected error"} />
			</div>
		);
	}

	return (
		<SkeletonBox className={cx("flex items-center justify-center", className)} style={{ width, height }} when={data}>
			{({ result, interval }) => {
				if (result.isFailure()) {
					return <CellError message={result.error} />;
				}

				return (
					<StatArea
						appearance="thin"
						calculation={timestamp ?? "last"}
						height={height}
						interval={interval}
						points={result.value.at(0)?.values ?? []}
						width={width}
					/>
				);
			}}
		</SkeletonBox>
	);
};
